/**
 * Admin Shell Layout
 *
 * Wrapper for all admin pages with distinct internal tooling look.
 * Includes warning banner, sidebar, and support mode indicator.
 */

import { Outlet } from 'react-router-dom';
import { AdminSidebar } from './AdminSidebar';
import { SupportModeBanner } from './SupportModeBanner';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { cn } from '@/lib/utils';
import { AlertTriangle } from 'lucide-react';

export function AdminShell() {
  const { isInSupportMode } = useSupportMode();

  return (
    <div className="min-h-screen bg-[hsl(220,25%,7%)]">
      {/* Internal Use Warning Banner */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-amber-600 text-amber-950 px-4 py-1.5 text-center text-sm font-medium flex items-center justify-center gap-2">
        <AlertTriangle className="h-4 w-4" />
        <span>INTERNAL USE ONLY - All actions are logged</span>
        <AlertTriangle className="h-4 w-4" />
      </div>

      {/* Support Mode Banner (below warning) */}
      {isInSupportMode && (
        <div className="fixed top-8 left-0 right-0 z-50">
          <SupportModeBanner />
        </div>
      )}

      {/* Sidebar */}
      <AdminSidebar />

      {/* Main content area */}
      <main
        className={cn(
          'ml-56 min-h-screen transition-all duration-200',
          // Account for warning banner (banner is ~36px tall)
          'pt-12',
          // Account for support mode banner if active
          isInSupportMode && 'pt-24'
        )}
      >
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/**
 * Page header component for admin pages
 */
interface AdminPageHeaderProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}

export function AdminPageHeader({ title, description, actions }: AdminPageHeaderProps) {
  return (
    <div className="mb-6 flex items-start justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-white">{title}</h1>
        {description && (
          <p className="mt-1 text-sm text-gray-400">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Card component for admin pages
 */
interface AdminCardProps {
  children: React.ReactNode;
  className?: string;
}

export function AdminCard({ children, className }: AdminCardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-[hsl(220,15%,20%)] bg-[hsl(220,20%,10%)] p-4',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * Stat card for overview page
 */
interface AdminStatCardProps {
  label: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon?: React.ReactNode;
}

export function AdminStatCard({
  label,
  value,
  change,
  changeType = 'neutral',
  icon,
}: AdminStatCardProps) {
  return (
    <AdminCard className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-400">{label}</p>
        <p className="mt-1 text-2xl font-semibold text-white">{value}</p>
        {change && (
          <p
            className={cn(
              'mt-1 text-xs',
              changeType === 'positive' && 'text-green-400',
              changeType === 'negative' && 'text-red-400',
              changeType === 'neutral' && 'text-gray-500'
            )}
          >
            {change}
          </p>
        )}
      </div>
      {icon && (
        <div className="rounded-md bg-[hsl(220,15%,15%)] p-2 text-gray-400">
          {icon}
        </div>
      )}
    </AdminCard>
  );
}
